'use client'

import React from 'react'
import styles from './styles.module.css'
import Img from '../../../Img'
import SmartLink from '../../../SmartLink'
import SaleCountdown from '../../../SaleCampaign/SaleCountdown'
import useSaleClock from '../../../SaleCampaign/useSaleClock'
import DealPrice from '../../../SaleCampaign/DealPrice'
import { useRegionOffers } from '../../../RegionOffers'
import { useUrlRegion } from '../../../../utils/useCurrentRegion'
import { pathWithRegion } from '../../../../utils/regions'
import { buildCheckoutPageUrlFromProductSetId } from '../../../../utils/shopItems'
import {
  getCampaignPhase, getLimitedDeals, getLimitedDealState, dealPriceOf, isDealRunning, getDealGroups,
  PHASE_LIVE, PHASE_TEASER, PHASE_ENDED, DEAL_LIVE, DEAL_UPCOMING, msOf,
} from '../../../../lib/saleCampaign'
import { formatSaleRange, itemTypeOf, regularPriceOf, salePriceOf, savingOf } from '../saleFormat'

// A limited deal as { title, price, regular } for the hero, or null. Same price
// rules as SaleLimitedDeals: the backend price while live, the published deal
// price in the teaser, nothing while the backend price is not loaded or the
// deal is sold out.
function dealViewOf(campaign, nowMs, region, offers, deal) {
  const item = deal && (offers || []).find((o) => itemTypeOf(o) === deal.itemType)
  if (!item || !item.price) return null
  const state = getLimitedDealState(campaign, nowMs, region, deal, item)
  const regular = regularPriceOf(item)
  const title = deal.title || item.namePublicShop || item.name
  if (state === DEAL_LIVE && savingOf(item)) return { title, price: salePriceOf(item), regular }
  const published = dealPriceOf(deal, region, regular && regular.currency)
  if (state === DEAL_UPCOMING && published && regular && published.priceCents < regular.priceCents) {
    return { title, price: published, regular }
  }
  return null
}

// The headline deal on the left: the first one on sale now, else the first one
// announced, outside the deal sets shown in the side panel.
function headlineDealOf(campaign, nowMs, region, offers, skipGroups = []) {
  const deals = getLimitedDeals(campaign, region, nowMs).filter((d) => !skipGroups.includes(d.group || 'deals'))
  const deal = deals.find((d) => isDealRunning(campaign, d, nowMs)) || deals[0]
  return dealViewOf(campaign, nowMs, region, offers, deal)
}

// Sale campaign hero. Two looks from one section type:
//  - variant "page" (default): the top of /<region>/<campaign.route> — h1, dates,
//    big countdown, "3 ways to save" strip.
//  - variant "home": the homepage banner — shorter, CTA to the sale page.
// Renders NOTHING outside the campaign window, so it can sit on the homepage
// year-round and switch itself on/off with the dates in the `sale-campaign` doc.
//
// Copy comes from the campaign doc (`hero` / `home`), with the section's own
// fields as overrides. Image = section `imageCs` (a dataset image ref), or a
// side panel from `productImages`: photos tied to a deal set (`group`, e.g. the
// pedals, whose price is the headline on the left) on top, then one card per
// deal (`itemType`, e.g. the DFP15 and the Lite Formula wheel), each its own
// deal with its photo, price and its set's timer, so two deals never read as a
// bundle. A product without a deal in this region is left out (the Lite in the
// US). The left column keeps the other sets (headline price, timers).
function SaleHero({ saleCampaign: campaign, saleNowMs, saleClockOffsetMs, variant = 'page', imageCs, productImages, titleOverride }) {
  const { nowMs, mounted } = useSaleClock(saleNowMs, saleClockOffsetMs)
  const urlRegion = useUrlRegion()
  const { offers } = useRegionOffers()
  const phase = getCampaignPhase(campaign, nowMs)
  const isHome = variant === 'home'
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) {
    // The homepage banner simply disappears. The sale PAGE keeps a minimal hero
    // (one h1, a way out) so a late visitor or an old ad click never lands on a
    // blank page.
    if (isHome || !campaign) return null
    const ended = phase === PHASE_ENDED
    return (
      <section className={[styles.hero, styles.page, styles.off].join(' ')}>
        <div className={styles.inner}>
          <div className={styles.copy}>
            <h1 className={styles.title}>{campaign.name}</h1>
            <p className={styles.text}>
              {ended
                ? ((campaign.hero && campaign.hero.textEnded) || 'This sale has ended. Thank you to everyone who joined us.')
                : `Starts ${formatSaleRange(campaign.start, campaign.end)}.`}
            </p>
            <div className={styles.ctas}>
              <SmartLink href={urlRegion ? pathWithRegion('/shop', urlRegion) : '/shop'} className={styles.ctaPrimary}>Go to the shop</SmartLink>
            </div>
          </div>
        </div>
      </section>
    )
  }

  // Region overrides (`byRegion.<region>`) on top, e.g. US copy without the
  // Lite Formula wheel, which has no deal there.
  const baseCopy = (isHome ? campaign.home : campaign.hero) || {}
  const copy = { ...baseCopy, ...((baseCopy.byRegion && urlRegion && baseCopy.byRegion[urlRegion]) || {}) }
  const live = phase === PHASE_LIVE
  const salePath = `/${campaign.route}`
  // Region pages link inside their store; the bare homepage link 307s to the
  // visitor's store (commerce path), so no region guess is needed there.
  const saleHref = urlRegion ? pathWithRegion(salePath, urlRegion) : salePath
  const hasLimited = getLimitedDeals(campaign, urlRegion, nowMs).length > 0

  const title = titleOverride || (live ? copy.titleLive : copy.titleTeaser) || campaign.name
  const subtitle = live ? copy.subtitleLive : copy.subtitleTeaser
  const text = live ? copy.textLive : copy.textTeaser
  const ctaLabel = live ? (copy.ctaLive || 'Shop the deals') : (copy.ctaTeaser || 'See what is coming')
  // One timer per deal set (pedals, wheels), each to its own end, or its start
  // while it is still to come; a sale without sets has one timer for the sale.
  const allGroups = getDealGroups(campaign, nowMs, urlRegion)
  // Side panel: the photographed deals that run in this region, each with its
  // own set and timer; those sets' timers move from the left column into the
  // panel cards.
  const regionDeals = getLimitedDeals(campaign, urlRegion, nowMs)
  const panelItems = isHome ? [] : (Array.isArray(productImages) ? productImages : [])
    .map((p) => ({ p, deal: p && p.image && p.itemType ? regionDeals.find((d) => d.itemType === p.itemType) : null }))
    .map((x) => ({ ...x, group: x.deal ? allGroups.find((g) => g.key === (x.deal.group || 'deals')) : null }))
    .filter((x) => x.deal && x.group)
  const panelPhotos = isHome ? [] : (Array.isArray(productImages) ? productImages : [])
    .filter((p) => p && p.image && !p.itemType && p.group && regionDeals.some((d) => (d.group || 'deals') === p.group))
  const panelKeys = panelItems.map((x) => x.group.key)
  const groups = allGroups.filter((g) => !panelKeys.includes(g.key))
  const timers = groups.length
    ? groups.map((g) => ({ key: g.key, targetMs: g.targetMs, label: (g.live ? g.countdownLive : g.countdownTeaser) || null }))
    : [{ key: 'sale', targetMs: live ? msOf(campaign.end) : msOf(campaign.start), label: null }]
  const defaultLabel = live ? (copy.countdownLiveLabel || 'Sale ends in') : (copy.countdownTeaserLabel || 'Starts in')
  const Heading = isHome ? 'h2' : 'h1'
  const headline = isHome ? null : headlineDealOf(campaign, nowMs, urlRegion, offers, panelKeys)

  const actions = (
    <>
      {/* No left-column timer when every deal set has its own card timer. */}
      <div className={styles.countdownRow} hidden={!isHome && panelItems.length > 0 && groups.length === 0}>
        {timers.map((t) => (
          <SaleCountdown
            key={t.key}
            targetMs={t.targetMs}
            nowMs={nowMs}
            mounted={mounted}
            size={isHome || timers.length > 1 || panelItems.length ? 'md' : 'lg'}
            theme="dark"
            label={t.label || defaultLabel}
          />
        ))}
      </div>
      <div className={styles.ctas}>
        {(isHome || !live) && (
          <SmartLink href={isHome ? saleHref : '#limited-deals'} className={styles.ctaPrimary}>{isHome ? ctaLabel : (copy.ctaTeaserPage || 'See the deals')}</SmartLink>
        )}
        {!isHome && live && (
          <SmartLink href={hasLimited ? '#limited-deals' : '#all-deals'} className={styles.ctaPrimary}>{hasLimited ? (copy.ctaLimited || 'See the pedal deals') : ctaLabel}</SmartLink>
        )}
        {/* the full price grid is optional: no `ctaAll` copy, no link to it */}
        {!isHome && live && copy.ctaAll && (
          <SmartLink href="#all-deals" className={styles.ctaSecondary}>{copy.ctaAll}</SmartLink>
        )}
      </div>
    </>
  )

  return (
    <section className={[styles.hero, isHome ? styles.home : styles.page].join(' ')} aria-label={campaign.name}>
      <div className={styles.glow} aria-hidden="true"/>
      {/* Layout from the section content (known on the server), not from the
          deals resolved on the client clock, so the hero never reflows. */}
      <div className={[styles.inner, !isHome && Array.isArray(productImages) && productImages.length ? styles.innerPanel : null].filter(Boolean).join(' ')}>
        <div className={styles.copy}>
          <div className={styles.eyebrow}>
            <span className={styles.dot} aria-hidden="true"/>
            {live ? (copy.eyebrowLive || 'Live now') : (copy.eyebrowTeaser || 'Coming soon')}
            <span className={styles.dates}>{formatSaleRange(campaign.start, campaign.end)}</span>
          </div>
          <Heading className={styles.title}>{title}</Heading>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          {text && <p className={styles.text}>{text}</p>}
          {headline && (
            <div className={styles.headlineDeal}>
              <span className={styles.headlineName}>{headline.title}</span>
              <DealPrice price={headline.price} regular={headline.regular} size="xl"/>
            </div>
          )}
          {!isHome && actions}
          {Array.isArray(copy.bullets) && copy.bullets.length > 0 && (
            <ul className={styles.bullets}>
              {copy.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
        </div>
        {isHome && <div className={styles.homeActions}>{actions}</div>}
        {panelItems.length ? (
          <aside className={styles.panel} aria-label={campaign.name}>
            {panelPhotos.map((p) => (
              <div key={p._key || p.alt} className={[styles.panelPhoto, styles.panelPhotoWide].join(' ')}>
                <Img image={p.image} alt={p.alt || campaign.name} objectFit="contain" sizes="(min-width: 1024px) 40vw, 90vw"/>
              </div>
            ))}
            <div className={styles.panelItems}>
              {panelItems.map(({ p, deal, group }) => {
                const view = dealViewOf(campaign, nowMs, urlRegion, offers, deal)
                // Straight to the product's shop page, like the deal cards below.
                const item = (offers || []).find((o) => itemTypeOf(o) === deal.itemType)
                const productHref = item && item.productSetId ? buildCheckoutPageUrlFromProductSetId(item.productSetId, urlRegion) : null
                return (
                  <div key={p._key || deal.itemType} className={styles.panelItem}>
                    {group.title && <span className={styles.panelKicker}>{group.title}</span>}
                    <div className={styles.panelPhoto}>
                      <Img image={p.image} alt={p.alt || deal.title || campaign.name} objectFit="contain" sizes="(min-width: 1024px) 24vw, 90vw"/>
                    </div>
                    <span className={styles.panelName}>{(view && view.title) || deal.title || p.alt}</span>
                    {/* Same size as the headline price on the left. */}
                    {view && <DealPrice price={view.price} regular={view.regular} size="xl"/>}
                    <SaleCountdown
                      targetMs={group.targetMs}
                      nowMs={nowMs}
                      mounted={mounted}
                      size="md"
                      theme="dark"
                      label={(group.live ? group.countdownLive : group.countdownTeaser) || defaultLabel}
                    />
                    {productHref && (
                      <SmartLink href={productHref} className={styles.panelBuy}>
                        {group.live ? (copy.ctaDeal || 'Get this deal') : (copy.ctaDealTeaser || 'View product')}
                      </SmartLink>
                    )}
                  </div>
                )
              })}
            </div>
          </aside>
        ) : imageCs && imageCs.image && (
          <div className={styles.media}>
            <Img image={imageCs.image} alt={imageCs.alt || campaign.name} objectFit="contain" sizes="(min-width: 1024px) 55vw, 100vw"/>
          </div>
        )}
      </div>
    </section>
  )
}

export default SaleHero
